import {
  POST_EDITOR_BLOCK_INDENT_TYPES,
  POST_EDITOR_MIN_INDENT,
} from './post_editor_constants.js';
import { BODY_PARAGRAPH_ATTRS } from './post_editor_constants_paragraph.js';

export function indentFromNode(node) {
  const level = node?.attrs?.indent;
  if (typeof level === 'number' && level > POST_EDITOR_MIN_INDENT) return level;
  return POST_EDITOR_MIN_INDENT;
}

/** Apply `data-ps-indent` on a block DOM node (editor node views). */
export function syncIndentOnDom(element, indentLevel) {
  if (!(element instanceof HTMLElement)) return;
  const level =
    typeof indentLevel === 'number' && indentLevel > POST_EDITOR_MIN_INDENT
      ? indentLevel
      : POST_EDITOR_MIN_INDENT;
  if (level > POST_EDITOR_MIN_INDENT) {
    element.setAttribute('data-ps-indent', String(level));
  } else {
    element.removeAttribute('data-ps-indent');
  }
}

/** Merge inherited indent into attrs for newly inserted media blocks. */
export function mergeBlockIndentAttrs(state, attrs = {}) {
  if (attrs.indent != null && attrs.indent > POST_EDITOR_MIN_INDENT) return attrs;
  return {
    ...attrs,
    indent: resolveBlockIndentFromContext(state),
  };
}

/** Inherit indent from the current block or the nearest preceding indentable sibling. */
export function resolveBlockIndentFromContext(state) {
  const { $from } = state.selection;

  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (POST_EDITOR_BLOCK_INDENT_TYPES.includes(node.type.name)) {
      const level = indentFromNode(node);
      if (level > POST_EDITOR_MIN_INDENT) return level;
    }
  }

  for (let d = $from.depth; d > 0; d--) {
    const index = $from.index(d);
    if (index > 0) {
      const prev = $from.node(d).child(index - 1);
      if (POST_EDITOR_BLOCK_INDENT_TYPES.includes(prev.type.name)) {
        const level = indentFromNode(prev);
        if (level > POST_EDITOR_MIN_INDENT) return level;
      }
    }
  }

  const nodeBefore = $from.nodeBefore;
  if (nodeBefore && POST_EDITOR_BLOCK_INDENT_TYPES.includes(nodeBefore.type.name)) {
    const level = indentFromNode(nodeBefore);
    if (level > POST_EDITOR_MIN_INDENT) return level;
  }

  return POST_EDITOR_MIN_INDENT;
}

/** Indent on a block container (resource table, blockquote, etc.) at `blockDepth`. */
export function resolveIndentFromBlockDepth($from, blockDepth) {
  if (blockDepth < 0 || blockDepth > $from.depth) return POST_EDITOR_MIN_INDENT;
  const node = $from.node(blockDepth);
  if (POST_EDITOR_BLOCK_INDENT_TYPES.includes(node.type.name)) {
    return indentFromNode(node);
  }
  return POST_EDITOR_MIN_INDENT;
}

/**
 * Top-to-bottom indent: first paragraph or block at the start of a list item sets
 * the level for everything below it in that item.
 */
export function resolveIndentFromTopOfListItem(listItemNode) {
  if (!listItemNode) return POST_EDITOR_MIN_INDENT;
  for (let i = 0; i < listItemNode.childCount; i++) {
    const child = listItemNode.child(i);
    if (child.type.name === 'paragraph') {
      return indentFromNode(child);
    }
    if (POST_EDITOR_BLOCK_INDENT_TYPES.includes(child.type.name)) {
      return indentFromNode(child);
    }
  }
  return POST_EDITOR_MIN_INDENT;
}

/**
 * Top-to-bottom indent for content at `blockDepth`: use the first paragraph or
 * block above it in the same parent (not the block's own indent).
 */
export function resolveIndentAboveBlock($from, blockDepth) {
  if (blockDepth < 0 || blockDepth > $from.depth) return POST_EDITOR_MIN_INDENT;

  let indent = POST_EDITOR_MIN_INDENT;
  const blockIndex = $from.index(blockDepth);
  const parentDepth = blockDepth - 1;

  if (parentDepth >= 0) {
    const parent = $from.node(parentDepth);
    for (let i = 0; i < blockIndex; i++) {
      const child = parent.child(i);
      if (child.type.name === 'paragraph') {
        indent = Math.max(indent, indentFromNode(child));
        return indent;
      }
      if (POST_EDITOR_BLOCK_INDENT_TYPES.includes(child.type.name)) {
        indent = Math.max(indent, indentFromNode(child));
        return indent;
      }
    }
  }

  for (let d = blockDepth - 1; d > 0; d--) {
    const name = $from.node(d).type.name;
    if (name === 'bulletList' || name === 'orderedList') {
      indent = Math.max(indent, indentFromNode($from.node(d)));
    }
    if (name === 'listItem') {
      indent = Math.max(indent, resolveIndentFromTopOfListItem($from.node(d)));
    }
  }

  return indent;
}

/**
 * Indent for a body paragraph inserted (or focused) after a block container — blockquote,
 * resource table, etc. Uses the container's indent first, then top-to-bottom from above.
 */
export function resolveIndentForInsertAfterBlock($from, blockDepth) {
  if (blockDepth < 0 || blockDepth > $from.depth) return POST_EDITOR_MIN_INDENT;
  const block = $from.node(blockDepth);
  if (POST_EDITOR_BLOCK_INDENT_TYPES.includes(block.type.name)) {
    const level = indentFromNode(block);
    if (level > POST_EDITOR_MIN_INDENT) return level;
  }
  return resolveIndentAboveBlock($from, blockDepth);
}

/** Re-stamp moved blocks so they follow the parent indent (top-to-bottom). */
export function reindentBlocksToLevel(blocks, indentLevel) {
  return blocks.map((node) => {
    if (!POST_EDITOR_BLOCK_INDENT_TYPES.includes(node.type.name)) return node;
    const indent =
      typeof indentLevel === 'number' && indentLevel > POST_EDITOR_MIN_INDENT
        ? indentLevel
        : POST_EDITOR_MIN_INDENT;
    if ((node.attrs.indent || 0) === indent) return node;
    return node.type.create({ ...node.attrs, indent }, node.content);
  });
}

/** Body paragraph attrs with visual indent (for siblings after an indented block). */
export function buildBodyParagraphAttrs(indentLevel = POST_EDITOR_MIN_INDENT) {
  const indent =
    typeof indentLevel === 'number' && indentLevel > POST_EDITOR_MIN_INDENT
      ? indentLevel
      : POST_EDITOR_MIN_INDENT;
  return { ...BODY_PARAGRAPH_ATTRS, indent };
}

export function createBodyParagraphWithIndent(schema, indentLevel = POST_EDITOR_MIN_INDENT) {
  const paragraphType = schema.nodes.paragraph;
  if (!paragraphType) return null;
  return paragraphType.create(buildBodyParagraphAttrs(indentLevel));
}

/** Ensure a body paragraph at `paragraphPos` carries `indentLevel` (no-op when level is 0). */
export function applyIndentToParagraphNode(tr, paragraphPos, indentLevel) {
  const indent =
    typeof indentLevel === 'number' && indentLevel > POST_EDITOR_MIN_INDENT
      ? indentLevel
      : POST_EDITOR_MIN_INDENT;
  if (indent <= POST_EDITOR_MIN_INDENT) return tr;

  const node = tr.doc.nodeAt(paragraphPos);
  if (!node || node.type.name !== 'paragraph') return tr;
  if ((node.attrs.indent || 0) === indent) return tr;

  tr.setNodeMarkup(paragraphPos, undefined, { ...node.attrs, indent });
  return tr;
}

/** resourceTable insert payload with indent inherited from the cursor context. */
export function buildResourceTableInsertContent(state, rows) {
  return {
    type: 'resourceTable',
    attrs: {
      indent: resolveBlockIndentFromContext(state),
    },
    content: rows,
  };
}

/** Pathstone blockquote attrs with indent inherited from the cursor context. */
export function buildBlockquoteAttrs(state, baseAttrs = {}) {
  return {
    ...baseAttrs,
    indent: resolveBlockIndentFromContext(state),
  };
}

/** Strip paragraph-level indent inside a blockquote (indent lives on the blockquote). */
export function clearIndentOnBlockquoteChildren(tr, blockquotePos, blockquoteNode) {
  let modified = false;
  blockquoteNode.forEach((child, offset) => {
    if (child.type.name !== 'paragraph') return;
    if (!child.attrs.indent || child.attrs.indent <= POST_EDITOR_MIN_INDENT) return;
    tr.setNodeMarkup(blockquotePos + 1 + offset, undefined, {
      ...child.attrs,
      indent: POST_EDITOR_MIN_INDENT,
    });
    modified = true;
  });
  return modified;
}

/** Move visual indent from inner paragraphs onto the blockquote (same model as lists). */
export function normalizeIndentedBlockquotesInRange(tr, from, to) {
  let modified = false;

  tr.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name !== 'blockquote') return;

    let quoteIndent = indentFromNode(node);
    node.forEach((child) => {
      if (child.type.name !== 'paragraph') return;
      const paraIndent = indentFromNode(child);
      if (paraIndent > quoteIndent) quoteIndent = paraIndent;
    });

    if (quoteIndent > POST_EDITOR_MIN_INDENT && (node.attrs.indent || 0) !== quoteIndent) {
      tr.setNodeMarkup(pos, null, { ...node.attrs, indent: quoteIndent });
      modified = true;
    }

    const updated = tr.doc.nodeAt(pos);
    if (updated) {
      modified = clearIndentOnBlockquoteChildren(tr, pos, updated) || modified;
    }

    return false;
  });

  return modified;
}
